// Mounts Stripe's card and wallet UI and coordinates payment submission with the PetRescue server.
// Authorizes the displayed attempt before Stripe confirmation and shares readiness with host UI.
import { registerFunctionalComponent } from 'js/lib/components/component'
import { requestNative } from 'js/lib/api/api-request'
import { notifyBugsnag } from 'js/lib/bugsnag'
import { on } from 'js/lib/events'
import { CHECKOUT_STATE_EVENT, CHECKOUT_STATE_REQUEST_EVENT } from './events'

registerFunctionalComponent('.js-c-payments-stripe-checkout', 'StripeCheckout', initStripeCheckout)

const PAYMENT_FORM_LOAD_ERROR_MESSAGE = 'Payment form failed to load. Please refresh the page to try again.'
const PAYMENT_LOADING_ERROR_MESSAGE = 'Payment form is still loading. Please wait a moment and try again.'
const PAYMENT_ELEMENT_OPTIONS = {
  layout: {
    type: 'accordion',
    defaultCollapsed: false,
    radios: false,
    spacedAccordionItems: false,
  },
  fields: {
    billingDetails: {
      email: 'never',
      name: 'never',
      phone: 'never',
    },
  },
}

function initStripeCheckout(component) {
  const dom = buildDom(component.element)
  const config = {
    clientSecret: dom.root.dataset.clientSecret,
    publishableKey: window.PetRescue.env.stripe.publishableKey,
  }
  const state = {
    expressCheckoutElement: null,
    paymentElement: null,
    stripeCheckout: null,
    checkoutActions: null,
    unmounted: false,
    isSubmitting: false,
    ready: false,
  }

  component.useHandler(() => setupSubmission(dom, state))
  component.useHandler(() => setupStripeElements(dom, config, state))
}

function buildDom(root) {
  const expressMountPoint = root.querySelector('.js-c-payments-stripe-checkout-express')
  const paymentMountPoint = root.querySelector('.js-c-payments-stripe-checkout-element')
  const expressSkeleton = root.querySelector('.js-c-payments-stripe-checkout-express-skeleton')
  const paymentSkeleton = root.querySelector('.js-c-payments-stripe-checkout-element-skeleton')

  const expressWrapper = root.querySelector('.js-c-payments-stripe-checkout-express-wrapper')

  return {
    root,
    expressWrapper,
    expressMountPoint,
    paymentMountPoint,
    expressSkeleton,
    paymentSkeleton,
    error: root.querySelector('.js-c-payments-stripe-checkout-error'),
    errorMessage: root.querySelector('.js-c-payments-stripe-checkout-error-message'),
  }
}

function setupStripeElements(dom, config, state) {
  if (!hasRequiredMountPoints(dom)) {
    reportMissingMountPoints(dom)
    renderLoadError(dom, PAYMENT_FORM_LOAD_ERROR_MESSAGE)
    return () => cleanupStripeElements(dom, state)
  }

  initializeStripeCheckout(dom, config, state)
  return () => cleanupStripeElements(dom, state)
}

// Load Stripe and initialize the remote checkout before mounting its payment UI.
// Unmount checks prevent a replaced widget from mounting the resulting payment UI.
async function initializeStripeCheckout(dom, config, state) {
  try {
    const { loadStripe } = await import('@stripe/stripe-js')
    const stripe = await loadStripe(config.publishableKey)
    if (state.unmounted) return

    const checkout = await stripe.initCheckout({
      clientSecret: config.clientSecret,
      elementsOptions: {
        appearance: {
          theme: 'custom',
          variables: {
            fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
            borderRadius: '8px',
            spacingGridRow: '24px',
            colorText: '#262626',
            colorPrimary: '#262626', // this is to hide the accordion tab label
            colorBackground: 'transparent',
          },
          rules: {
            '.Label': {
              fontWeight: '600',
              fontSize: '16px',
              marginBottom: '8px',

            },
            '.Input': {
              border: '2px solid #262626',
            },
            '.Input::placeholder': {
              color: 'transparent',
            },
            '.Input:focus': {
              border: '2px solid #262626',
              boxShadow: '0 0 0 3px #3b82f6',
            },
            '.AccordionItem': {
              border: 'none',
              boxShadow: 'none',
              padding: '0 3px 12px 3px',
            },
            '.TabIcon': {
              color: '#404040',
            },
            '.CheckboxInput': {
              border: '2px solid #262626',
              borderRadius: '4px',
              backgroundColor: '#e5e5e5',
            },
            '.CheckboxInput--checked': {
              border: '2px solid #262626',
              borderRadius: '4px',
              backgroundColor: '#e5e5e5',
            },
            '.Text--terms': {
              color: '#404040',
              fontSize: '14px',
              textWrap: 'pretty',
            },
          },
        },
      },
    })

    if (state.unmounted) return

    state.stripeCheckout = checkout
    state.checkoutActions = null

    mountStripeElements(dom, state)
  } catch (error) {
    if (state.unmounted) return
    state.ready = false
    publishState(dom)
    notifyBugsnag(checkoutError(error))
    renderLoadError(dom, PAYMENT_FORM_LOAD_ERROR_MESSAGE)
  }
}

function mountStripeElements(dom, state) {
  state.expressCheckoutElement = state.stripeCheckout.createExpressCheckoutElement()
  state.expressCheckoutElement.on('confirm', event => handlePayment(dom, state, event))
  state.expressCheckoutElement.on('ready', ({ availablePaymentMethods }) => {
    if (state.unmounted) return
    if (!availablePaymentMethods) {
      if (dom.expressWrapper) dom.expressWrapper.style.display = 'none'
      return
    }
    if (dom.expressSkeleton) dom.expressSkeleton.style.display = 'none'
    dom.expressMountPoint.hidden = false
  })
  state.expressCheckoutElement.mount(dom.expressMountPoint)

  state.paymentElement = state.stripeCheckout.createPaymentElement(PAYMENT_ELEMENT_OPTIONS)
  state.paymentElement.on('ready', () => {
    if (state.unmounted) return
    if (dom.paymentSkeleton) dom.paymentSkeleton.style.display = 'none'
    dom.paymentMountPoint.hidden = false
    state.ready = true
    publishState(dom)
  })
  state.paymentElement.on('loaderror', () => {
    if (state.unmounted) return
    state.ready = false
    publishState(dom)
    renderLoadError(dom, PAYMENT_FORM_LOAD_ERROR_MESSAGE)
  })
  state.paymentElement.mount(dom.paymentMountPoint)
}

// Stop answering readiness requests before notifying buttons, then destroy the provider UI.
function cleanupStripeElements(dom, state) {
  state.unmounted = true
  state.ready = false
  state.checkoutActions = null
  publishState(dom)
  if (state.expressCheckoutElement) state.expressCheckoutElement.destroy()
  if (state.paymentElement) state.paymentElement.destroy()
}

// Answer state queries and intercept only buttons for this connected purchase widget.
// The cleanup removes both delegated listeners when the component is replaced.
function setupSubmission(dom, state) {
  const reference = dom.root.dataset.checkoutReference
  const removeRequest = on(CHECKOUT_STATE_REQUEST_EVENT, document, event => {
    if (event.detail.checkoutReference !== reference || state.unmounted || !dom.root.isConnected) return
    event.detail.respond({ ready: state.ready, submitting: state.isSubmitting })
  })
  const removeClick = on('click', '.js-c-payments-checkout-submit', (event, button) => {
    if (button.dataset.checkoutReference !== reference || state.unmounted || !dom.root.isConnected) return
    event.preventDefault()
    handlePayment(dom, state)
  })
  publishState(dom)

  return () => {
    removeRequest()
    removeClick()
  }
}

// Card and wallet payments share the same two-step handoff: first ask our server
// to authorize the displayed attempt, then let Stripe collect the money. Keep
// another click or an unmounted widget from starting a second confirmation.
async function handlePayment(dom, state, expressCheckoutConfirmEvent) {
  if (state.unmounted || state.isSubmitting || !dom.root.isConnected) {
    expressCheckoutConfirmEvent?.paymentFailed()
    return
  }

  if (!state.stripeCheckout || (!expressCheckoutConfirmEvent && !state.ready)) {
    expressCheckoutConfirmEvent?.paymentFailed()
    renderCheckoutError(dom, { message: PAYMENT_LOADING_ERROR_MESSAGE })
    return
  }

  state.isSubmitting = true
  publishState(dom)
  clearError(dom)

  try {
    await submitToPlatform(dom)
    if (state.unmounted) throw new Error('Payment form was closed before confirmation')

    const error = await confirmCheckoutPayment(state, expressCheckoutConfirmEvent)
    if (error) {
      expressCheckoutConfirmEvent?.paymentFailed()
      if (!state.unmounted) handleStripeError(error, dom)
    }
  } catch (serverError) {
    expressCheckoutConfirmEvent?.paymentFailed()
    if (!state.unmounted) handleServerError(serverError, dom)
  } finally {
    state.isSubmitting = false
    if (!state.unmounted) publishState(dom)
  }
}

// Submit the identity next to the displayed Stripe form, not a cached reference
// from an earlier step. The server rejects stale attempts before Stripe confirms.
// This POST may observe Stripe and persist submission, but success is authorization, not collection.
async function submitToPlatform(dom) {
  if (!dom.root.dataset.attemptReference || !dom.root.dataset.endpoint) {
    throw new Error('Payment attempt reference is missing. Please refresh and try again.')
  }
  await requestNative({
    url: dom.root.dataset.endpoint,
    type: 'POST',
    data: { attempt_reference: dom.root.dataset.attemptReference },
    accept: 'json',
  })
}

// Hands confirmation to Stripe, including the wallet's authorization event when
// present. A successful payment normally redirects to our payment resolver.
async function confirmCheckoutPayment(state, expressCheckoutConfirmEvent) {
  const actions = await loadCheckoutActions(state)
  if (state.unmounted) throw new Error('Payment form was closed before confirmation')

  const { error } = await actions.confirm(expressCheckoutConfirmEvent ? { expressCheckoutConfirmEvent } : undefined)
  return error
}

async function loadCheckoutActions(state) {
  if (state.checkoutActions) return state.checkoutActions

  const result = await state.stripeCheckout.loadActions()

  if (result.type === 'error') {
    throw result.error || new Error('Failed to initialize checkout actions')
  }

  if (!state.unmounted) state.checkoutActions = result.actions
  return result.actions
}

function handleStripeError(error, dom) {
  // Checkout confirm errors are buyer-facing messages, not PaymentIntent error types.
  if (typeof error?.message === 'string' && error.message.trim()) {
    renderCheckoutError(dom, { message: error.message })
    return
  }

  renderCheckoutError(dom)
  console.error(error)
  notifyBugsnag(checkoutError(error))
}

function handleServerError(serverError, dom) {
  renderCheckoutError(dom, buildServerErrorPayload(serverError))

  if (hasDisplayableServerErrors(serverError)) return

  console.error(serverError)
  notifyBugsnag(checkoutError(serverError))
}

// Notify interested buttons to query the live provider; this event carries identity, not a state snapshot.
function publishState(dom) {
  document.dispatchEvent(new CustomEvent(CHECKOUT_STATE_EVENT, {
    detail: { checkoutReference: dom.root.dataset.checkoutReference },
  }))
}

function renderLoadError(dom, message) {
  if (dom.paymentSkeleton) dom.paymentSkeleton.style.display = 'none'
  if (dom.expressSkeleton) dom.expressSkeleton.style.display = 'none'
  renderCheckoutError(dom, { message })
}

function clearError(dom) {
  dom.error.hidden = true
  dom.errorMessage.textContent = ''
}

function renderCheckoutError(dom, error) {
  clearError(dom)
  dom.errorMessage.textContent = error?.message || error?.errors?.join(' ') || 'Unable to process your payment. Please try again.'
  dom.error.hidden = false
}

function buildServerErrorPayload(serverError) {
  const errors = Array.isArray(serverError?.errors) ? serverError.errors.filter(Boolean) : []
  const message = typeof serverError?.message === 'string' && serverError.message.trim() ? serverError.message : null

  return {
    errors,
    message,
  }
}

function hasDisplayableServerErrors(serverError) {
  return Boolean(serverError?.error_type)
    || Array.isArray(serverError?.errors) && serverError.errors.length > 0
    || Boolean(typeof serverError?.message === 'string' && serverError.message.trim())
}

function checkoutError(error) {
  if (error instanceof Error) return error

  const message = typeof error?.message === 'string' ? error.message : error
  return new Error(typeof message === 'string' && message.trim() ? message : 'Unexpected checkout error')
}

function hasRequiredMountPoints(dom) {
  return Boolean(dom.expressMountPoint && dom.paymentMountPoint)
}

function reportMissingMountPoints(dom) {
  const missingMountPoints = [!dom.expressMountPoint && 'express', !dom.paymentMountPoint && 'payment']
    .filter(Boolean)
    .join(', ')

  notifyBugsnag(new Error(`Stripe checkout mount point missing: ${missingMountPoints}`))
}
